const SITE = 'https://www.morganlegalny.com';

/**
 * Полоса «о нас писали» и наград — то, что было на боевой главной картинками
 * и не переехало: шесть логотипов прессы и шесть значков наград.
 *
 * Здесь они текстом, а не логотипами, и на то две причины. Логотип чужого
 * издания — это утверждение о факте, а реклама юруслуг в Нью-Йорке живёт под
 * правилом 7.1: не вводить в заблуждение. Поэтому каждое издание в верхнем ряду
 * ведёт на конкретный материал, где участие фирмы видно из текста — проверено
 * поштучно 2026-08-29. Шести логотипов с боевой главной здесь нет намеренно:
 * по NBC, CNN, Forbes и Fortune ни в одном из 4891 поста подтверждения не
 * нашлось. Вторая причина проще: шесть растровых логотипов в первом экране
 * стоят дороже строки текста.
 *
 * Награда — тоже утверждение, поэтому «Forbes Business Council» назван полностью:
 * это платное членство, а не публикация, и в ряду прессы ему не место.
 */
interface Feature {
  outlet: string;
  detail: string;
  /** Материал, где участие фирмы видно: наш пост либо сама запись эфира. */
  href: string;
}

const FEATURED: Feature[] = [
  {
    outlet: 'FOX',
    detail: 'LiveNOW from FOX',
    href: `${SITE}/video-game-actors-go-on-strike/`,
  },
  {
    outlet: 'ABC',
    detail: 'WTVM 9 · WBBJ 7',
    href: `${SITE}/abc-9-ga-wtvm-morgan-legal-group/`,
  },
  {
    outlet: 'Scripps News',
    detail: 'America on the Docket',
    href: `${SITE}/holding-parents-accountable-in-school-shootings/`,
  },
  {
    outlet: 'Business Insider',
    detail: 'Severance ruling',
    href: `${SITE}/legal-victory-for-ex-twitter-employee-could-spark-more-severance-claims/`,
  },
  {
    // Станция названа прямо, а не «NBC News»: KFYR-TV — аффилиат NBC в Бисмарке,
    // и это не то же самое, что сеть. Запись эфира лежит на канале агентства,
    // которое размещало сюжет, поэтому ссылка ведёт туда — другого адреса у него
    // нет, а утверждение без адреса проверить нельзя.
    outlet: 'NBC · KFYR-TV',
    detail: 'Wills & trusts segment',
    href: 'https://www.youtube.com/watch?v=EmgByN1qHUs',
  },
];

const AWARDS: Array<{ name: string; detail: string; href?: string }> = [
  { name: 'Super Lawyers', detail: 'Seventh consecutive year' },
  { name: 'Best Lawyers', detail: '2023' },
  { name: 'BBB Accredited', detail: 'A+ rating' },
  { name: 'Justia Rating', detail: '10.0 Top Attorney' },
  { name: 'Expertise.com', detail: 'Best Estate Attorneys' },
  { name: 'Three Best Rated', detail: '2024' },
  {
    // Forbes сам помечает такие материалы «COUNCIL POST | Membership (fee-based)»,
    // а в подписи стоит «Former Forbes Councils Member» — значит и «2023», и слово
    // «Member» были неверны. Ссылка ведёт на саму статью: авторская колонка на
    // forbes.com — факт, «нас показывали на Forbes» — нет.
    name: 'Forbes Business Council',
    detail: 'Council post, 2022',
    href: 'https://www.forbes.com/councils/forbesbusinesscouncil/2022/05/11/what-business-leaders-and-entrepreneurs-need-to-know-about-trusts/',
  },
];

function Diamond() {
  return (
    <span
      aria-hidden="true"
      className="inline-block w-1 h-1 bg-gold rotate-45 shrink-0"
    />
  );
}

export default function PressStrip() {
  // Лента дублируется: вторая копия въезжает ровно тогда, когда первая уходит,
  // поэтому шов не виден. Копия скрыта от чтения с экрана — иначе список
  // читается дважды. При отключённой анимации копия убирается совсем: лента
  // не едет, и посетитель видел бы каждое издание дважды.
  const featured = [...FEATURED, ...FEATURED];
  const awards = [...AWARDS, ...AWARDS];

  return (
    <section
      className="bg-cream border-y border-navy/10 overflow-hidden"
      aria-label="Media coverage and recognition"
    >
      <div className="relative py-3">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-24 z-10 bg-gradient-to-r from-cream to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-24 z-10 bg-gradient-to-l from-cream to-transparent" />
        <div className="flex whitespace-nowrap animate-marquee motion-reduce:animate-none motion-reduce:flex-wrap motion-reduce:justify-center">
          {featured.map((f, i) => (
            <a
              key={`${f.outlet}-${i}`}
              href={f.href}
              className={`group flex items-center gap-3 px-6 sm:px-8 shrink-0${
                i >= FEATURED.length ? ' motion-reduce:hidden' : ''
              }`}
              aria-hidden={i >= FEATURED.length ? 'true' : undefined}
              tabIndex={i >= FEATURED.length ? -1 : undefined}
            >
              <Diamond />
              <span className="font-body text-[0.72rem] font-semibold tracking-[0.12em] uppercase text-navy group-hover:text-gold transition-colors">
                {f.outlet}
              </span>
              <span className="font-body text-[0.65rem] tracking-[0.08em] text-navy/50">
                {f.detail}
              </span>
            </a>
          ))}
        </div>
      </div>

      <div className="relative py-3 border-t border-navy/10">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-24 z-10 bg-gradient-to-r from-cream to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-24 z-10 bg-gradient-to-l from-cream to-transparent" />
        <div className="flex whitespace-nowrap animate-marquee-reverse motion-reduce:animate-none motion-reduce:flex-wrap motion-reduce:justify-center">
          {awards.map((a, i) => {
            const dup = i >= AWARDS.length;
            const inner = (
              <>
                <Diamond />
                <span className="font-body text-[0.72rem] font-semibold tracking-[0.12em] uppercase text-navy group-hover:text-gold transition-colors">
                  {a.name}
                </span>
                <span className="font-body text-[0.65rem] tracking-[0.08em] text-gold">
                  · {a.detail}
                </span>
              </>
            );
            const cls = `group flex items-center gap-3 px-6 sm:px-8 shrink-0${
              dup ? ' motion-reduce:hidden' : ''
            }`;
            return a.href ? (
              <a
                key={`${a.name}-${i}`}
                href={a.href}
                target="_blank"
                rel="noopener noreferrer"
                className={cls}
                aria-hidden={dup ? 'true' : undefined}
                tabIndex={dup ? -1 : undefined}
              >
                {inner}
              </a>
            ) : (
              <div key={`${a.name}-${i}`} className={cls} aria-hidden={dup ? 'true' : undefined}>
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
